import type { QueryClient } from '@tanstack/react-query'
import type { Regions } from '~/lib/adb/client'
import {
  chapterEventsQuery,
  regionsQuery,
  type EventsSeed,
} from '~/lib/adb/chapter'

/**
 * Loader helpers. They read through the query cache (so revisiting a page, or
 * going from a chapter list to an event, doesn't refetch what Query already
 * has) and never throw: a flaky ADB API must not fail the prerender build (or
 * a client navigation); the page renders a loading state and the on-mount
 * refresh tries again.
 */
export async function loadSeed(
  queryClient: QueryClient,
  pageId: number,
): Promise<EventsSeed> {
  const query = chapterEventsQuery(pageId)
  try {
    const events = await queryClient.ensureQueryData(query)
    return {
      pageId,
      events,
      failed: false,
      // When the list was fetched (not now: it may come from the cache), so
      // the server and the client filter "upcoming" against the same time.
      now:
        queryClient.getQueryState(query.queryKey)?.dataUpdatedAt ?? Date.now(),
    }
  } catch (e) {
    console.warn(`events for ${pageId} unavailable`, e)
    return { pageId, events: [], failed: true, now: Date.now() }
  }
}

export async function loadRegions(
  queryClient: QueryClient,
): Promise<Regions | null> {
  try {
    return await queryClient.ensureQueryData(regionsQuery())
  } catch (e) {
    console.warn('regions unavailable', e)
    return null
  }
}
